import React, { useState, useRef, useEffect } from "react";
import { Button, Form, Input, InputGroup, Popover, PopoverHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useSearchParams, useLocation } from "react-router";
import { connect } from "react-redux";
import {
  getAllByCriteria,
  startMainSearch,
  endMainSearch,
} from "../../../../../../../actions";
import Spinner from "../../../../../../common/loader/components/spinner";

const ExpandableSearch = ({
  startSearch,
  getAllByCriteria,
  endSearch,
  searchQuerying,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [searchParams] = useSearchParams();
  const [criteria, setCriteria] = useState("");
  const [popError, setPopError] = useState(false);
  const [initialized, setInitialized] = useState(true);
  const location = useLocation();
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const searchParamsObject = Object.fromEntries([...searchParams]);
    if (
      location.pathname === "/search" &&
      initialized &&
      searchParamsObject.input &&
      searchParamsObject.input.length > 2
    ) {
      const loadSearchResults = async () => {
        startSearch();
        try {
          await getAllByCriteria(searchParamsObject.input);
        } finally {
          endSearch();
        }
      };
      loadSearchResults();
    }
  }, [initialized]);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (criteria.length === 0 && isMobile) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [criteria, isMobile]);

  const onSubmit = async (e) => {
    e && e.preventDefault();
    if (criteria.length > 2) {
      setPopError(false);
      startSearch();
      try {
        await getAllByCriteria(criteria);
      } finally {
        endSearch();
      }
    } else {
      setPopError(true);
    }
  };

  const onChange = (e) => {
    setCriteria(e.target.value);
  };

  const toggleSearch = () => {
    setIsExpanded(!isExpanded);
  };

  const togglePopError = () => {
    setPopError(!popError);
  };

  const icon = searchQuerying ? (
    <Spinner />
  ) : (
    <i className="fa fa-search" />
  );

  const showBar = true;

  return (
    <div ref={containerRef} className="expandable-search">
      {!showBar ? (
        <Button
          color="link"
          className="search-icon-btn nav-link-icon"
          onClick={toggleSearch}
          aria-label={t("search")}
        >
          <i className="fa fa-search" />
          <span>{t("search")}</span>
        </Button>
      ) : (
        <Form onSubmit={onSubmit} className="expanded-search-form">
          <InputGroup>
            <Input
              innerRef={inputRef}
              onChange={onChange}
              className="form-control search-input"
              type="search"
              placeholder={t("search-placeholder")}
              value={criteria}
              aria-label="Search"
            />
            {popError && inputRef.current && (
              <Popover
                placement="bottom"
                isOpen={popError}
                target={inputRef.current}
                toggle={togglePopError}
              >
                <PopoverHeader>{t("err-search-length")}</PopoverHeader>
              </Popover>
            )}
            <Button
              type="submit"
              className="btn btn-primary btn-search"
              aria-label={t("search")}
            >
              {icon}
            </Button>
          </InputGroup>
        </Form>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getAllByCriteria: (criteria) => dispatch(getAllByCriteria(criteria)),
  startSearch: () => dispatch(startMainSearch()),
  endSearch: () => dispatch(endMainSearch()),
});

const mapStateToProps = (state) => ({
  searchQuerying: state.search.mainFilterQuerying,
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpandableSearch);
